import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Icon } from '../Icon';
import { DocsSidebar } from './DocsSidebar';

interface DocsLayoutProps {
    children: ReactNode;
}

/**
 * Layout dédié à la documentation — accessible **sans authentification**.
 *
 * Structure :
 *   <header>     : mini-navbar (logo + CTA Se connecter/Dashboard)
 *   <aside>      : sidebar des sections/pages
 *   <main>       : contenu Markdown
 *
 * La sidebar se transforme en drawer sur mobile.
 */
export function DocsLayout({ children }: DocsLayoutProps) {
    const { user, loading } = useAuth();
    const [menuOpen, setMenuOpen] = useState(false);

    // Fermer le drawer lors d'un changement de route côté enfant
    useEffect(() => {
        if (!menuOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setMenuOpen(false);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [menuOpen]);

    return (
        <div className="docs-layout">
            <header className="docs-topbar">
                <div className="docs-topbar-inner">
                    <button
                        type="button"
                        className="docs-menu-toggle"
                        onClick={() => setMenuOpen(true)}
                        aria-label="Ouvrir le menu de documentation"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                            <line x1="3" y1="6" x2="21" y2="6" />
                            <line x1="3" y1="12" x2="21" y2="12" />
                            <line x1="3" y1="18" x2="21" y2="18" />
                        </svg>
                    </button>

                    <Link to="/docs" className="docs-brand">
                        <img src="/logoQuotio.png" alt="Quotio" />
                        <span>Documentation</span>
                    </Link>

                    <nav className="docs-topbar-nav">
                        <a href="/" className="docs-topbar-link">Site</a>
                        {loading ? null : user ? (
                            <Link to="/dashboard" className="btn-primary docs-topbar-cta">
                                <Icon name="grid" size={14} />
                                Mon dashboard
                            </Link>
                        ) : (
                            <>
                                <Link to="/login" className="docs-topbar-link">Se connecter</Link>
                                <Link to="/signup" className="btn-primary docs-topbar-cta">
                                    <Icon name="sparkles" size={14} />
                                    Essayer Quotio
                                </Link>
                            </>
                        )}
                    </nav>
                </div>
            </header>

            <div className="docs-body">
                <aside
                    className={`docs-sidebar-wrap${menuOpen ? ' is-open' : ''}`}
                    aria-hidden={!menuOpen && undefined}
                >
                    <DocsSidebar onNavigate={() => setMenuOpen(false)} />
                </aside>
                {menuOpen && (
                    <div
                        className="docs-backdrop"
                        onClick={() => setMenuOpen(false)}
                        aria-hidden
                    />
                )}

                <main className="docs-main">{children}</main>
            </div>
        </div>
    );
}
